<template>
	<Row class="page-container-row">
		<Col
			:span="24"
			:offset="0"
			:xl="{ span: 18, offset: 3 }"
			:xxl="{ span: 12, offset: 6 }"
		>
			<PageContainer>
				<BackButtonHeader :header="$t('events.detailPage.title')" back-to="events" />
				<Spin :spinning="isLoadingDetails">
					<div v-if="eventDetails">
						<Descriptions :column="1" bordered size="small">
							<DescriptionsItem :label="$t('events.detailPage.level')">
								<AlertLevelDisplay :level="eventDetails.level" />
							</DescriptionsItem>
							<DescriptionsItem :label="$t('events.detailPage.source')">
								{{ eventDetails.source }}
							</DescriptionsItem>
							<DescriptionsItem :label="$t('events.detailPage.subkey')">
								{{ eventDetails.subkey || '-' }}
							</DescriptionsItem>
							<DescriptionsItem :label="$t('events.detailPage.created')">
								{{ formattedCreated }}
							</DescriptionsItem>
							<DescriptionsItem v-if="eventDetails.message" :label="$t('events.detailPage.message')">
								{{ eventDetails.message }}
							</DescriptionsItem>
							<template v-if="sessionStore.debug && parsedParams && parsedParams.trace_data">
								<DescriptionsItem :label="$t('events.detailPage.stacktrace')">
									<Textarea :value="parsedParams.trace_data.stacktrace" :rows="8" readonly />
								</DescriptionsItem>
								<DescriptionsItem
									v-for="(frame, index) in parsedParams.trace_data.frames"
									:key="index"
									:label="`Frame ${index + 1}: ${frame.function}`"
								>
									<div class="frame-details">
										<div><strong>File:</strong> {{ frame.filename }}</div>
										<div><strong>Line:</strong> {{ frame.lineno }}</div>
										<div><strong>Function:</strong> {{ frame.function }}</div>
										<div v-if="frame.locals && frame.locals.length > 0" class="frame-locals">
											<strong>Local Variables:</strong>
											<ul>
												<li v-for="local in frame.locals" :key="local.name">
													<strong>{{ local.name }}:</strong> {{ local.value }}
												</li>
											</ul>
										</div>
									</div>
								</DescriptionsItem>
							</template>
							<DescriptionsItem
								v-else-if="sessionStore.debug && eventDetails.params"
								:label="$t('events.detailPage.params')"
							>
								<Textarea :value="eventDetails.params" :rows="12" readonly />
							</DescriptionsItem>
						</Descriptions>
					</div>
				</Spin>
			</PageContainer>
		</Col>
	</Row>
</template>

<script>
import { ref, computed, onMounted } from 'vue';
import { useRoute } from 'vue-router';
import { Row, Col, Textarea, Spin, Descriptions } from 'ant-design-vue';

const DescriptionsItem = Descriptions.Item;

import ApiEvents from '@/api/ApiEvents';

import { useSessionStore } from '@/stores/session';

import { datetimeToString } from '@/components/common/dateUtils';

import PageContainer from '@/components/common/PageContainer';
import AlertLevelDisplay from '@/components/common/AlertLevelDisplay';
import BackButtonHeader from '@/components/common/BackButtonHeader.vue';


export default {
	components: {
		Row, Col, Textarea, Spin, Descriptions, DescriptionsItem,
		PageContainer, AlertLevelDisplay, BackButtonHeader,
	},
	setup() {
		const route = useRoute();
		const sessionStore = useSessionStore();
		const eventDetails = ref(null);
		const isLoadingDetails = ref(true);

		const formattedCreated = computed(() => {
			if (!eventDetails.value || !eventDetails.value.created) {
				return '';
			}
			return datetimeToString(eventDetails.value.created);
		});

		const parsedParams = computed(() => {
			if (!eventDetails.value || !eventDetails.value.params) {
				return null;
			}
			try {
				const params = JSON.parse(eventDetails.value.params);
				return params && params.trace_data ? params : null;
			} catch (e) {
				return null;
			}
		});

		onMounted(() => {
			const id = Number(route.params.id);
			ApiEvents.eventGroupGetLastEvent(id, data => {
				isLoadingDetails.value = false;
				eventDetails.value = data;
			}, () => {
				isLoadingDetails.value = false;
			});
		});

		return {
			sessionStore,
			eventDetails,
			isLoadingDetails,
			formattedCreated,
			parsedParams,
		};
	},
};
</script>

<style scoped>
.frame-details {
	font-size: 13px;
	line-height: 1.5;
}
.frame-locals ul {
	margin: 4px 0 0;
	padding-left: 18px;
}
</style>
